import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api-client";
import { useAuthLite } from "@/context/AuthLite";
import type {
  CallingCallLogSeriesResponse,
  CallingCallLogSeriesScope,
  CallingCallersResponse,
} from "@shared/api";

export type CallLogChartRangeKey = "7" | "30" | "90";

export type CallLogChartFilterState = {
  scope: CallingCallLogSeriesScope;
  range: CallLogChartRangeKey;
  selectedUserEmail: string;
};

function fmtDayLabel(dayKey: string): string {
  const [y, m, d] = dayKey.split("-").map((x) => parseInt(x, 10));
  if (!y || !m || !d) return dayKey;
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  return dt.toLocaleDateString(undefined, { month: "short", day: "2-digit" });
}

function fmtTooltipLabel(dayKey: string): string {
  const [y, m, d] = dayKey.split("-").map((x) => parseInt(x, 10));
  if (!y || !m || !d) return dayKey;
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  return dt.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

function WidgetError({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
      {message}
    </div>
  );
}

export type CallLogsDailyChartCardProps = {
  className?: string;
  filters: CallLogChartFilterState;
  onFiltersChange: (patch: Partial<CallLogChartFilterState>) => void;
};

export function CallLogsDailyChartCard({ className, filters, onFiltersChange }: CallLogsDailyChartCardProps) {
  const { t } = useTranslation();
  const { user } = useAuthLite();
  const { scope, range, selectedUserEmail } = filters;

  const callersQ = useQuery({
    queryKey: ["calling", "callers"],
    queryFn: async () => {
      const r = await apiFetch("/api/crm/calling/callers?days=120", { credentials: "include" });
      if (!r.ok) throw new Error("callers_fetch");
      return r.json() as Promise<CallingCallersResponse>;
    },
    staleTime: 5 * 60_000,
  });

  const effectiveUserEmail = scope === "user" ? (selectedUserEmail || user?.email || "") : "";

  const seriesQ = useQuery({
    queryKey: ["calling", "call-logs-series", scope, effectiveUserEmail, range],
    queryFn: async () => {
      const q = new URLSearchParams();
      q.set("scope", scope);
      q.set("days", range);
      if (scope === "user" && effectiveUserEmail) q.set("userEmail", effectiveUserEmail);
      const r = await apiFetch(`/api/crm/calling/call-logs/series?${q.toString()}`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error("series_fetch");
      return r.json() as Promise<CallingCallLogSeriesResponse>;
    },
    staleTime: 60_000,
  });

  const callerOptions = useMemo(() => {
    const raw = callersQ.data?.users ?? [];
    const options = raw.map((u) => ({ email: u.email, label: u.label || u.email }));
    const current = user?.email;
    if (current && !options.some((o) => o.email.toLowerCase() === current.toLowerCase())) {
      options.unshift({ email: current, label: current });
    }
    return options;
  }, [callersQ.data?.users, user?.email]);

  const chartData = useMemo(() => {
    return (seriesQ.data?.series ?? []).map((p) => ({
      day: p.day,
      label: fmtDayLabel(p.day),
      count: p.count,
    }));
  }, [seriesQ.data?.series]);

  const total = useMemo(() => {
    return (seriesQ.data?.series ?? []).reduce((acc, p) => acc + (p.count || 0), 0);
  }, [seriesQ.data?.series]);

  const subtitle = useMemo(() => {
    const from = seriesQ.data?.dateFrom;
    const to = seriesQ.data?.dateTo;
    if (!from || !to) return "";
    return `${from} – ${to} (Europe/Bratislava)`;
  }, [seriesQ.data?.dateFrom, seriesQ.data?.dateTo]);

  return (
    <Card className={cn("border bg-card/60", className)}>
      <CardHeader className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <CardTitle className="text-base">{t("calling.callLogChartTitle")}</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              {subtitle || t("calling.callLogChartSubtitle")}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <ToggleGroup
              type="single"
              value={scope}
              onValueChange={(v) => {
                if (v === "me" || v === "user" || v === "team") onFiltersChange({ scope: v });
              }}
              variant="outline"
              size="sm"
              className="justify-start"
            >
              <ToggleGroupItem value="me">{t("calling.callLogChartScopeMe")}</ToggleGroupItem>
              <ToggleGroupItem value="user">{t("calling.callLogChartScopeUser")}</ToggleGroupItem>
              <ToggleGroupItem value="team">{t("calling.callLogChartScopeTeam")}</ToggleGroupItem>
            </ToggleGroup>

            <ToggleGroup
              type="single"
              value={range}
              onValueChange={(v) => {
                if (v === "7" || v === "30" || v === "90") onFiltersChange({ range: v });
              }}
              variant="outline"
              size="sm"
              className="justify-start"
            >
              <ToggleGroupItem value="7">7</ToggleGroupItem>
              <ToggleGroupItem value="30">30</ToggleGroupItem>
              <ToggleGroupItem value="90">90</ToggleGroupItem>
            </ToggleGroup>

            {scope === "user" && (
              <Select
                value={effectiveUserEmail}
                onValueChange={(v) => onFiltersChange({ selectedUserEmail: v })}
              >
                <SelectTrigger className="h-9 min-w-[220px]">
                  <SelectValue placeholder={t("calling.callLogChartSelectUser")} />
                </SelectTrigger>
                <SelectContent>
                  {callerOptions.map((o) => (
                    <SelectItem key={o.email} value={o.email}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>
            {t("calling.callLogChartTotal")}:{" "}
            <span className="font-mono text-foreground tabular-nums">{total.toLocaleString()}</span>
          </span>
          {callersQ.isLoading ? (
            <span className="font-mono">{t("calling.callLogChartLoadingUsers")}</span>
          ) : callersQ.isError ? (
            <span className="text-destructive">{t("calling.callLogChartUsersLoadError")}</span>
          ) : (
            <span className="font-mono">
              {t("calling.callLogChartUsersCount", { count: (callersQ.data?.users ?? []).length })}
            </span>
          )}
        </div>
      </CardHeader>

      <CardContent className="h-56 min-h-[220px]">
        {seriesQ.isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : seriesQ.isError ? (
          <WidgetError message={t("calling.callLogChartLoadError")} />
        ) : chartData.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("calling.callLogChartNoData")}</p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="callsFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(214 88% 45%)" stopOpacity={0.32} />
                  <stop offset="100%" stopColor="hsl(214 88% 45%)" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="day"
                tickFormatter={(v: string) => fmtDayLabel(v)}
                tick={{ fontSize: 11 }}
                minTickGap={18}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={34} />
              <Tooltip
                formatter={(v: number) => [Number(v).toLocaleString(), t("calling.callLogChartCalls")]}
                labelFormatter={(v: string) => fmtTooltipLabel(v)}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="hsl(214 88% 45%)"
                strokeWidth={2}
                fill="url(#callsFill)"
                dot={false}
                activeDot={{ r: 4 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
